import { escapeRegExp } from "views/email-template-form/components/escape-regexp";

interface ParsedVariableToken {
  key: string;
  fallback: string | null;
}

interface FallbackAwareVariable {
  key: string;
  requires_fallback?: boolean;
}

export const parseVariableToken = (inner: string): ParsedVariableToken => {
  const pipeIndex = inner.indexOf("|");
  if (pipeIndex === -1) return { key: inner.trim(), fallback: null };

  const fallback = inner.slice(pipeIndex + 1).trim();
  return {
    key: inner.slice(0, pipeIndex).trim(),
    fallback: fallback === "" ? null : fallback
  };
};

export const formatVariableToken = (
  key: string,
  fallback?: string | null
): string => {
  const trimmed = fallback?.trim();
  return trimmed ? `{{${key}|${trimmed}}}` : `{{${key}}}`;
};

export const validateFallbackText = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return "Fallback text is required";
  if (trimmed.includes("}"))
    return "Fallback text can't contain the } character";
  return null;
};

// System SMS bodies are saved as Liquid: the API rewrites {{key|text}} into
// {{ key | fallback: "text" }} and only matches text without "|" or ":", and
// drops '"'. Reject those here instead of failing on save.
export const validateLiquidFallbackText = (value: string): string | null => {
  const error = validateFallbackText(value);
  if (error) return error;
  if (/[|:"]/.test(value))
    return "Fallback text can't contain the |, : or \" characters";
  return null;
};

export const findMissingFallbackKeys = (
  text: string,
  variables: FallbackAwareVariable[]
): string[] => {
  const requiredKeys = variables
    .filter((v) => v.requires_fallback)
    .map((v) => v.key);
  if (requiredKeys.length === 0) return [];

  const missing = new Set<string>();
  for (const key of requiredKeys) {
    const esc = escapeRegExp(key);
    const opener = new RegExp(`\\{\\{\\s*${esc}\\s*(?=[|}])`, "g");
    const validToken = new RegExp(
      `^\\{\\{\\s*${esc}\\s*(?:\\|([^{}]*))?\\}\\}`
    );

    let match = opener.exec(text);
    while (match) {
      const escaped = match.index > 0 && text[match.index - 1] === "\\";
      if (!escaped) {
        const parsed = validToken.exec(text.slice(match.index));
        const fallback = parsed?.[1]?.trim();
        if (!fallback) missing.add(key);
      }
      match = opener.exec(text);
    }
  }

  return [...missing];
};
